import React, { useState, useMemo, useEffect } from 'react';
import { Icons } from '../Icons';
import { Appointment, Patient, Dentist, Procedure } from '../../types';
import { APPT_STATUS } from '../../constants/appointmentStatus';
import { getAppointmentTimeRange } from '../../utils/appointmentUtils';

const HOLIDAYS: Record<string, string> = {
  '2025-12-25': 'Christmas Day',
  '2025-12-26': 'Boxing Day',
  '2026-01-01': 'New Year\'s Day',
};

interface CalendarViewProps {
  appointments: Appointment[];
  patients: Patient[];
  dentists: Dentist[];
  procedures: Procedure[];
  initialDate?: string;
  onEditAppointment: (appointment: Appointment) => void;
  onCreateAppointment: (date: string, time: string) => void;
}

type ViewMode = 'day' | 'week' | 'month';

export const CalendarView: React.FC<CalendarViewProps> = ({
  appointments,
  patients,
  dentists,
  procedures,
  initialDate,
  onEditAppointment,
  onCreateAppointment,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = useState(() =>
    initialDate ? new Date(initialDate) : new Date()
  );
  const [filters, setFilters] = useState({ dentist: 'all', status: 'all', type: 'all' });

  // Update current date when a new initialDate is provided (e.g., after seed data loads)
  useEffect(() => {
    if (initialDate) {
      setCurrentDate(new Date(initialDate));
    }
  }, [initialDate]);

  const hours = Array.from({ length: 12 }, (_, i) => i + 8); // 8 AM to 7 PM

  // Navigate between time periods
  const navigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setMonth(currentDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get dates for the current view
  const getViewDates = (): Date[] => {
    if (viewMode === 'day') {
      return [currentDate];
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
      startOfWeek.setDate(diff);
      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        return date;
      });
    } else {
      // Month view
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const dates: Date[] = [];

      // Add days from previous month to fill first week
      const firstDayOfWeek = firstDay.getDay();
      const daysFromPrevMonth = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
      for (let i = daysFromPrevMonth; i > 0; i--) {
        const date = new Date(firstDay);
        date.setDate(firstDay.getDate() - i);
        dates.push(date);
      }

      // Add all days of current month
      for (let day = 1; day <= lastDay.getDate(); day++) {
        dates.push(new Date(year, month, day));
      }

      // Add days from next month to fill last week
      const remainingDays = 7 - (dates.length % 7);
      if (remainingDays < 7) {
        for (let i = 1; i <= remainingDays; i++) {
          dates.push(new Date(year, month + 1, i));
        }
      }

      return dates;
    }
  };

  const viewDates = useMemo(() => getViewDates(), [currentDate, viewMode]);

  const matchesFilters = (appt: Appointment) => {
    if (filters.dentist !== 'all' && appt.providerId !== filters.dentist) return false;
    if (filters.status !== 'all' && appt.status !== filters.status) return false;
    if (filters.type !== 'all' && appt.type !== filters.type) return false;
    return true;
  };

  // Filter appointments for a specific date and hour
  const getAppointmentsForSlot = (date: Date, hour?: number) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(appt => {
      if (appt.date !== dateStr) return false;
      if (!matchesFilters(appt)) return false;

      if (hour !== undefined && viewMode !== 'month') {
        const { startTime } = getAppointmentTimeRange(appt);
        const apptHour = startTime.getHours();
        return apptHour === hour;
      }

      return true;
    });
  };

  // Format current period
  const formatCurrentPeriod = () => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } else if (viewMode === 'week') {
      const endDate = new Date(viewDates[viewDates.length - 1]);
      return `${viewDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getHolidayLabel = (date: Date) => {
    const key = date.toISOString().split('T')[0];
    return HOLIDAYS[key] || null;
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  return (
    <div className="space-y-6">
      {/* Header + Filters */}
      <div className="space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center space-x-4">
            <h2 className="text-3xl font-black">My Calendar · {formatCurrentPeriod()}</h2>
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-bold"
            >
              Today
            </button>
          </div>

          <div className="flex items-center space-x-3">
            {/* View Mode Selector */}
            <div className="flex bg-slate-100 rounded-xl p-1">
              {(['day', 'week', 'month'] as ViewMode[]).map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold capitalize transition-all ${
                    viewMode === mode ? 'bg-white shadow-sm' : 'text-slate-500'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate('prev')}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <Icons.ChevronLeft />
              </button>
              <button
                onClick={() => navigate('next')}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <Icons.ChevronRight />
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select
            className="w-full border-2 rounded-xl p-3 text-sm font-bold bg-white"
            value={filters.dentist}
            onChange={(e) => setFilters(prev => ({ ...prev, dentist: e.target.value }))}
          >
            <option value="all">All dentists</option>
            {dentists.map(d => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>

          <select
            className="w-full border-2 rounded-xl p-3 text-sm font-bold bg-white"
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">All statuses</option>
            {Object.keys(APPT_STATUS).map(statusKey => (
              <option key={statusKey} value={statusKey}>
                {APPT_STATUS[statusKey].label}
              </option>
            ))}
          </select>

          <select
            className="w-full border-2 rounded-xl p-3 text-sm font-bold bg-white"
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
          >
            <option value="all">All procedures</option>
            {procedures.map(proc => (
              <option key={proc.id} value={proc.name}>
                {proc.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-3xl border shadow-sm overflow-hidden">
        {viewMode === 'month' ? (
          // Month View
          <div>
            {/* Week day headers */}
            <div className="grid grid-cols-7 border-b bg-slate-50">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="p-3 text-center text-xs font-bold text-slate-400 uppercase">
                  {day}
                </div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7">
              {viewDates.map((date, idx) => {
                const dayAppts = getAppointmentsForSlot(date);
                const isCurrentMonthDate = isCurrentMonth(date);
                const isTodayDate = isToday(date);
                const holidayLabel = getHolidayLabel(date);

                const dateStr = date.toISOString().split('T')[0];

                return (
                  <div
                    key={idx}
                    className={`min-h-[120px] border-r border-b p-2 cursor-pointer transition-colors group ${
                      !isCurrentMonthDate ? 'bg-slate-50' : ''
                    } ${holidayLabel ? 'bg-rose-50 hover:bg-rose-100' : 'hover:bg-blue-50'} ${
                      holidayLabel ? 'border-rose-100' : ''
                    } ${(idx + 1) % 7 === 0 ? 'border-r-0' : ''}`}
                    onClick={(e) => {
                      if (holidayLabel) return;
                      // Only create appointment if clicking empty space (not an existing appointment)
                      if (e.target === e.currentTarget || !(e.target as HTMLElement).closest('.appointment-item')) {
                        onCreateAppointment(dateStr, '09:00');
                      }
                    }}
                  >
                    <div
                      className={`text-sm font-bold mb-2 ${
                        isTodayDate
                          ? 'bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center'
                          : isCurrentMonthDate
                          ? 'text-slate-900'
                          : 'text-slate-400'
                      }`}
                    >
                      {date.getDate()}
                    </div>
                    {holidayLabel && (
                      <div className="text-[11px] font-bold text-rose-600 mb-2">Holiday: {holidayLabel}</div>
                    )}
                    {dayAppts.length === 0 && (
                      <div className={`opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold text-center mt-8 ${
                        holidayLabel ? 'text-rose-600' : 'text-blue-600'
                      }`}>
                        + New
                      </div>
                    )}
                    <div className="space-y-1">
                      {dayAppts.slice(0, 3).map(appt => {
                        const patient = patients.find(p => p.id === appt.patientId);
                        const status = APPT_STATUS[appt.status];
                        return (
                          <div
                            key={appt.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditAppointment(appt);
                            }}
                            className={`appointment-item text-xs p-1 rounded cursor-pointer hover:opacity-80 ${status.color}`}
                          >
                            <div className="font-bold truncate">{appt.time} {patient?.name}</div>
                          </div>
                        );
                      })}
                      {dayAppts.length > 3 && (
                        <div className="text-xs text-slate-500 font-bold">+{dayAppts.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Day/Week View
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Time headers */}
              <div className={`grid ${viewMode === 'day' ? 'grid-cols-[80px_1fr]' : 'grid-cols-[80px_repeat(7,1fr)]'} border-b bg-slate-50`}>
                <div className="p-3"></div>
                {viewDates.map((date, idx) => (
                  <div key={idx} className="p-3 text-center border-l">
                    <div className={`text-xs font-bold ${isToday(date) ? 'text-blue-600' : 'text-slate-400'} uppercase`}>
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`text-2xl font-black mt-1 ${isToday(date) ? 'text-blue-600' : 'text-slate-900'}`}>
                      {date.getDate()}
                    </div>
                    {getHolidayLabel(date) && (
                      <div className="text-[11px] font-bold text-rose-600 mt-1">{getHolidayLabel(date)}</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Time slots */}
              {hours.map(hour => (
                <div key={hour} className={`grid ${viewMode === 'day' ? 'grid-cols-[80px_1fr]' : 'grid-cols-[80px_repeat(7,1fr)]'} border-b min-h-[80px]`}>
                  <div className="p-3 text-xs font-bold text-slate-400">
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                  {viewDates.map((date, idx) => {
                    const slotAppts = getAppointmentsForSlot(date, hour);
                    const dateStr = date.toISOString().split('T')[0];
                    const timeStr = `${hour.toString().padStart(2, '0')}:00`;
                    const holidayLabel = getHolidayLabel(date);

                    return (
                      <div
                        key={idx}
                        className={`border-l p-2 relative transition-colors group ${holidayLabel ? 'bg-rose-50 cursor-not-allowed' : 'hover:bg-blue-50 cursor-pointer'}`}
                        onClick={(e) => {
                          if (holidayLabel) return;
                          // Only create appointment if clicking empty space (not an existing appointment)
                          if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.appointment-slot') === null) {
                            onCreateAppointment(dateStr, timeStr);
                          }
                        }}
                      >
                        {/* Click hint for empty slots */}
                        {slotAppts.length === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <div className={`text-xs font-bold ${holidayLabel ? 'text-rose-600' : 'text-blue-600'}`}>
                              {holidayLabel ? `Holiday: ${holidayLabel}` : '+ New Appointment'}
                            </div>
                          </div>
                        )}

                        <div className="space-y-1">
                          {slotAppts.map(appt => {
                            const patient = patients.find(p => p.id === appt.patientId);
                            const dentist = dentists.find(d => d.id === appt.providerId);
                            const status = APPT_STATUS[appt.status];
                            const { endTime } = getAppointmentTimeRange(appt);

                            return (
                              <div
                                key={appt.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditAppointment(appt);
                                }}
                                className={`appointment-slot rounded-lg p-2 cursor-pointer hover:shadow-lg transition-all hover:scale-105 animate-scaleIn ${status.color}`}
                              >
                                <div className="font-bold text-xs truncate">{patient?.name}</div>
                                <div className="text-[10px] truncate">{appt.type}</div>
                                <div className="text-[10px] truncate">{dentist?.name}</div>
                                <div className="text-[10px]">{appt.time} - {endTime.toTimeString().slice(0, 5)}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
